import { unwrapData } from '@wapharma/shared';
import { parseApiErrorTraceId, setLastTraceId } from '@wapharma/shared';
import { recordReproBreadcrumb, sanitizeReproValue } from '@wapharma/shared';
import { clearTokens, getToken, setAccessToken } from './auth';
import { isAuthPublicPath, isAuthPublicReturnPath } from './auth/publicPaths';
import { HttpError, messageFromFailedResponse } from './apiErrors';
import { API_BASE } from './lib/apiBase';

const base = API_BASE;

const fetchCredentials: RequestCredentials = 'include';


function headers(token?: string, json = false): Record<string, string> {
  const h: Record<string, string> = {};
  if (token) h.Authorization = `Bearer ${token}`;
  if (json) h['Content-Type'] = 'application/json';
  return h;
}

/** Same-origin path + query only; safe for ?next= redirect. */
export function safeReturnPathFromLocation(): string {
  if (typeof window === 'undefined') return '/';
  const { pathname, search } = window.location;
  if (!pathname.startsWith('/') || pathname.startsWith('//')) return '/';
  if (isAuthPublicReturnPath(pathname + search)) return '/';
  return pathname + search;
}

function redirectToLogin(): void {
  if (typeof window !== 'undefined') {
    const path = window.location.pathname;
    if (isAuthPublicPath(path)) return;
  }
  const next = safeReturnPathFromLocation();
  window.location.assign(`/login?next=${encodeURIComponent(next)}`);
}

let refreshInFlight: Promise<boolean> | null = null;
let lastTokenRefreshAt = 0;

const DEFAULT_FETCH_TIMEOUT_MS = 15_000;
const REFRESH_FETCH_TIMEOUT_MS = 10_000;

async function fetchWithTimeout(
  url: string,
  init: RequestInit,
  timeoutMs = DEFAULT_FETCH_TIMEOUT_MS,
): Promise<Response> {
  if (timeoutMs <= 0) {
    return fetch(url, init);
  }
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);
  const upstream = init.signal;
  const onUpstreamAbort = () => controller.abort();
  upstream?.addEventListener('abort', onUpstreamAbort);
  try {
    return await fetch(url, { ...init, signal: controller.signal });
  } finally {
    window.clearTimeout(timeoutId);
    upstream?.removeEventListener('abort', onUpstreamAbort);
  }
}

/** Same as automatic refresh on 401 — call to prolong session before expiry. */
export async function refreshAccessToken(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    try {
      const res = await fetchWithTimeout(
        `${base}/auth/refresh`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({}),
          credentials: fetchCredentials,
        },
        REFRESH_FETCH_TIMEOUT_MS,
      );
      if (!res.ok) {
        if (import.meta.env.DEV) {
          console.warn('[auth/refresh]', res.status, await res.clone().text().catch(() => ''));
        }
        return false;
      }
      const json = (await res.json()) as { data?: { accessToken: string } };
      const d = unwrapData(json) as { accessToken: string };
      if (!d?.accessToken) return false;
      setAccessToken(d.accessToken);
      const now = Date.now();
      if (now - lastTokenRefreshAt > 250) {
        lastTokenRefreshAt = now;
        window.dispatchEvent(new CustomEvent('wapharma:token-refreshed'));
      }
      return true;
    } catch (err) {
      if (import.meta.env.DEV) {
        console.warn('[auth/refresh]', err);
      }
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

async function tryRefresh(): Promise<boolean> {
  return refreshAccessToken();
}

type FetchAuthOptions = {
  method?: string;
  headers?: HeadersInit;
  body?: BodyInit | null;
  token?: string;
  signal?: AbortSignal;
  timeoutMs?: number;
  _retry?: boolean;
};

async function fetchWithAuth(path: string, opts: FetchAuthOptions): Promise<Response> {
  const url = `${base}${path}`;
  const h = new Headers(opts.headers);
  if (opts.token) h.set('Authorization', `Bearer ${opts.token}`);

  const startedAt = typeof performance !== 'undefined' ? performance.now() : Date.now();
  const res = await fetchWithTimeout(
    url,
    {
      method: opts.method ?? 'GET',
      headers: h,
      body: opts.body ?? undefined,
      signal: opts.signal,
      credentials: fetchCredentials,
    },
    opts.timeoutMs ?? DEFAULT_FETCH_TIMEOUT_MS,
  );
  const durationMs = Math.round(
    (typeof performance !== 'undefined' ? performance.now() : Date.now()) - startedAt,
  );

  if (!import.meta.env.DEV) {
    recordReproBreadcrumb({
      t: 'api',
      method: String(opts.method ?? 'GET'),
      path,
      status: res.status,
      durationMs,
    });
  }

  if (res.status === 401 && opts.token && !opts._retry) {
    const refreshed = await tryRefresh();
    if (refreshed) {
      const nextTok = getToken();
      return fetchWithAuth(path, { ...opts, token: nextTok ?? undefined, _retry: true, signal: opts.signal });
    }
    clearTokens();
    redirectToLogin();
    const text = await res.text();
    throw new HttpError(messageFromFailedResponse(401, text), 401, text);
  }

  return res;
}

async function throwIfNotOk(res: Response): Promise<void> {
  if (res.ok) return;
  const text = await res.text();
  try {
    const body = text.trim().startsWith('{') ? JSON.parse(text) : null;
    setLastTraceId(parseApiErrorTraceId(body, res));
  } catch {
    setLastTraceId(parseApiErrorTraceId(null, res));
  }
  throw new HttpError(messageFromFailedResponse(res.status, text), res.status, text);
}

export async function apiGet<T>(path: string, token?: string, signal?: AbortSignal): Promise<T> {
  const res = await fetchWithAuth(path, { token, signal });
  await throwIfNotOk(res);
  return (await res.json()) as T;
}

export async function apiPost<T>(
  path: string,
  body: unknown,
  token?: string,
  signal?: AbortSignal,
  timeoutMs?: number,
): Promise<T> {
  if (!import.meta.env.DEV) {
    recordReproBreadcrumb({ t: 'api', method: 'POST', path, req: sanitizeReproValue(body) });
  }
  const res = await fetchWithAuth(path, {
    method: 'POST',
    headers: headers(token, true),
    body: JSON.stringify(body),
    token,
    signal,
    timeoutMs,
  });
  await throwIfNotOk(res);
  return (await res.json()) as T;
}

/** multipart/form-data (do not set Content-Type — browser sets boundary). */
export async function apiPostForm<T>(
  path: string,
  form: FormData,
  token?: string,
  timeoutMs?: number,
  signal?: AbortSignal,
): Promise<T> {
  const h: Record<string, string> = {};
  if (token) h.Authorization = `Bearer ${token}`;
  const res = await fetchWithAuth(path, {
    method: 'POST',
    headers: h,
    body: form,
    token,
    timeoutMs,
    signal,
  });
  await throwIfNotOk(res);
  return res.json() as Promise<T>;
}

export async function apiPatch<T>(
  path: string,
  body: unknown,
  token?: string,
  signal?: AbortSignal,
): Promise<T> {
  if (!import.meta.env.DEV) {
    recordReproBreadcrumb({ t: 'api', method: 'PATCH', path, req: sanitizeReproValue(body) });
  }
  const res = await fetchWithAuth(path, {
    method: 'PATCH',
    headers: headers(token, true),
    body: JSON.stringify(body),
    token,
    signal,
  });
  await throwIfNotOk(res);
  return (await res.json()) as T;
}

export async function apiPut<T>(
  path: string,
  body: unknown,
  token?: string,
  signal?: AbortSignal,
): Promise<T> {
  if (!import.meta.env.DEV) {
    recordReproBreadcrumb({ t: 'api', method: 'PUT', path, req: sanitizeReproValue(body) });
  }
  const res = await fetchWithAuth(path, {
    method: 'PUT',
    headers: headers(token, true),
    body: JSON.stringify(body),
    token,
    signal,
  });
  await throwIfNotOk(res);
  return (await res.json()) as T;
}

export async function apiDelete(path: string, token?: string, signal?: AbortSignal): Promise<void> {
  const res = await fetchWithAuth(path, { method: 'DELETE', token, signal });
  await throwIfNotOk(res);
}

/** GET binary response with Bearer auth — triggers browser download */
export async function apiDownloadBlob(
  path: string,
  token: string,
  filename: string,
): Promise<void> {
  const res = await fetchWithAuth(path, { token });
  await throwIfNotOk(res);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** POST JSON → binary PDF download (document issuance). */
export async function apiPostDownloadBlob(
  path: string,
  body: unknown,
  token: string,
  fallbackFilename: string,
): Promise<void> {
  const res = await fetchWithAuth(path, {
    method: 'POST',
    headers: headers(token, true),
    body: JSON.stringify(body ?? {}),
    token,
  });
  await throwIfNotOk(res);
  const disposition = res.headers.get('content-disposition') ?? '';
  const matched = /filename="([^"]+)"/i.exec(disposition);
  const filename = matched?.[1]?.trim() || fallbackFilename;
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** GET raw CSV (non-JSON) with Bearer auth — triggers browser download */
export async function apiDownloadCsv(path: string, token: string, filename: string): Promise<void> {
  return apiDownloadBlob(path, token, filename);
}
